import { LoadingButton } from '@mui/lab';
import { alpha, Box, Card, Skeleton, Typography } from '@mui/material';
import { useTaxInqueryMutation } from '../../../../app/store';
import { immediateToast } from 'utils/toast';
import { Address, AddressLocation } from '../../types';

type AddressCardProps = {
    address?: Address;
    disabled?: boolean;
};

const addressTypeConfig: Record<
    Address['type'],
    { label: string; icon: string }
> = {
    home: {
        label: 'خانه',
        icon: 'fa-light fa-house',
    },
    company: {
        label: 'محل کار',
        icon: 'fa-light fa-building',
    },
};

const locationText = (value?: AddressLocation | null): string => {
    if (typeof value === 'string') return value;
    if (!value || typeof value !== 'object') return '';

    const candidate = value.name ?? value.label;
    return typeof candidate === 'string' ? candidate : '';
};

const AddressCard = ({ address, disabled }: AddressCardProps) => {
    if (!address || disabled) {
        return <AddressCardSkeleton />;
    }

    const cityName = locationText(address.city);
    const provinceFromCity =
        typeof address.city === 'object' && address.city
            ? locationText(address.city.province)
            : '';
    const provinceName = locationText(address.province) || provinceFromCity;
    const location = [provinceName, cityName].filter(Boolean).join('، ');
    const type = addressTypeConfig[address.type];

    return (
        <Card
            variant="outlined"
            sx={(theme) => ({
                width: '100%',
                p: { xs: 1.75, sm: 2.25 },
                borderRadius: 2.5,
                borderColor: alpha(theme.palette.text.primary, 0.09),
                bgcolor: 'background.paper',
                boxShadow:
                    theme.palette.mode === 'light'
                        ? `0 6px 22px ${alpha(theme.palette.common.black, 0.025)}`
                        : `0 8px 26px ${alpha(theme.palette.common.black, 0.12)}`,
                transition:
                    'border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
                '&:hover': {
                    borderColor: alpha(theme.palette.primary.main, 0.28),
                    boxShadow:
                        theme.palette.mode === 'light'
                            ? `0 10px 30px ${alpha(theme.palette.common.black, 0.045)}`
                            : `0 10px 30px ${alpha(theme.palette.common.black, 0.18)}`,
                },
            })}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    minWidth: 0,
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.15,
                        minWidth: 0,
                    }}
                >
                    <Box
                        sx={(theme) => ({
                            width: 38,
                            height: 38,
                            flexShrink: 0,
                            display: 'grid',
                            placeItems: 'center',
                            borderRadius: 1.6,
                            color:
                                theme.palette.mode === 'dark'
                                    ? theme.palette.primary.light
                                    : theme.palette.primary.main,
                            bgcolor: alpha(
                                theme.palette.primary.main,
                                theme.palette.mode === 'dark' ? 0.14 : 0.075,
                            ),
                            '& i': { fontSize: 16 },
                        })}
                    >
                        <i className={type.icon} />
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            className="font-yekan-heavy"
                            sx={{ fontSize: 14.5, color: 'text.primary' }}
                        >
                            {type.label}
                        </Typography>
                        {location && (
                            <Typography
                                variant="caption"
                                noWrap
                                sx={{ mt: 0.2, display: 'block', color: 'text.secondary' }}
                            >
                                {location}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {!address.taxcode && (
                    <TaxRequestButton address={address} compact />
                )}
            </Box>

            <Box
                sx={(theme) => ({
                    mt: 1.75,
                    px: { xs: 1.4, sm: 1.6 },
                    py: 1.35,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    borderRadius: 1.75,
                    bgcolor: alpha(
                        theme.palette.text.primary,
                        theme.palette.mode === 'dark' ? 0.035 : 0.022,
                    ),
                    border: '1px solid',
                    borderColor: alpha(theme.palette.text.primary, 0.055),
                })}
            >
                <Box
                    component="i"
                    className="fa-light fa-location-dot"
                    sx={{ mt: 0.4, color: 'text.secondary', fontSize: 15 }}
                />
                <Typography
                    sx={{
                        color: 'text.primary',
                        fontSize: 13,
                        lineHeight: 2,
                        wordBreak: 'break-word',
                    }}
                >
                    {address.address || 'نشانی ثبت نشده است'}
                </Typography>
            </Box>

            <Box
                sx={{
                    mt: 1.5,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                    gap: 1,
                }}
            >
                <AddressMeta
                    icon="fa-light fa-mailbox"
                    label="کد پستی"
                    value={address.postal || '—'}
                />
                <AddressMeta
                    icon="fa-light fa-phone-rotary"
                    label="تلفن ثابت"
                    value={address.telephone || '—'}
                />
                {address.taxcode && (
                    <AddressMeta
                        icon="fa-light fa-file-invoice"
                        label="کد مالیاتی"
                        value={address.taxcode}
                    />
                )}
            </Box>

        </Card>
    );
};

const AddressMeta = ({
    icon,
    label,
    value,
}: {
    icon: string;
    label: string;
    value: string;
}) => (
    <Box
        sx={(theme) => ({
            minWidth: 0,
            px: 1.25,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 0.9,
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: alpha(theme.palette.text.primary, 0.065),
        })}
    >
        <Box component="i" className={icon} sx={{ color: 'text.secondary', fontSize: 14 }} />
        <Box sx={{ minWidth: 0 }}>
            <Typography
                variant="caption"
                sx={{ display: 'block', color: 'text.secondary', fontSize: 10.5 }}
            >
                {label}
            </Typography>
            <Typography
                component="span"
                dir="ltr"
                sx={{ display: 'block', mt: 0.15, color: 'text.primary', fontSize: 12.25 }}
            >
                {value}
            </Typography>
        </Box>
    </Box>
);

const AddressCardSkeleton = () => (
    <Card
        variant="outlined"
        sx={{
            width: '100%',
            p: { xs: 1.75, sm: 2.25 },
            borderRadius: 2.5,
            borderColor: 'divider',
            boxShadow: 'none',
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <Skeleton variant="rounded" width={38} height={38} />
            <Box sx={{ flex: 1 }}>
                <Skeleton width={80} height={22} />
                <Skeleton width={130} height={18} />
            </Box>
        </Box>
        <Skeleton variant="rounded" sx={{ mt: 1.75 }} height={64} />
        <Box
            sx={{
                mt: 1.5,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: 1,
            }}
        >
            <Skeleton variant="rounded" height={52} />
            <Skeleton variant="rounded" height={52} />
        </Box>
    </Card>
);

const TaxRequestButton = ({
    address,
    compact = false,
}: {
    address: Address;
    compact?: boolean;
}) => {
    const [taxInquery, { isLoading, originalArgs }] = useTaxInqueryMutation();

    return (
        <LoadingButton
            fullWidth={!compact}
            loading={isLoading && originalArgs?.address_id === address.id}
            variant="outlined"
            size="small"
            startIcon={<i className="fa-light fa-file-invoice" />}
            sx={(theme) => ({
                minHeight: compact ? 34 : 38,
                flexShrink: 0,
                px: compact ? 1.1 : 1.5,
                whiteSpace: 'nowrap',
                fontSize: compact ? 10.75 : 12,
                color:
                    theme.palette.mode === 'dark'
                        ? theme.palette.primary.light
                        : theme.palette.primary.main,
                borderColor: alpha(
                    theme.palette.mode === 'dark'
                        ? theme.palette.primary.light
                        : theme.palette.primary.main,
                    theme.palette.mode === 'dark' ? 0.42 : 0.32,
                ),
                backgroundColor: alpha(
                    theme.palette.mode === 'dark'
                        ? theme.palette.primary.light
                        : theme.palette.primary.main,
                    theme.palette.mode === 'dark' ? 0.08 : 0.025,
                ),
                '&:hover': {
                    color:
                        theme.palette.mode === 'dark'
                            ? theme.palette.primary.light
                            : theme.palette.primary.dark,
                    borderColor: alpha(
                        theme.palette.mode === 'dark'
                            ? theme.palette.primary.light
                            : theme.palette.primary.main,
                        theme.palette.mode === 'dark' ? 0.66 : 0.5,
                    ),
                    backgroundColor: alpha(
                        theme.palette.mode === 'dark'
                            ? theme.palette.primary.light
                            : theme.palette.primary.main,
                        theme.palette.mode === 'dark' ? 0.13 : 0.055,
                    ),
                },
                '& .MuiLoadingButton-loadingIndicator': {
                    color: 'inherit',
                },
            })}
            onClick={() => {
                taxInquery({ address_id: address.id })
                    .unwrap()
                    .then(() => {
                        immediateToast('success', {
                            message: 'کد مالیاتی بروزرسانی شد',
                            timeout: 2000,
                        });
                    })
                    .catch(() => {
                        immediateToast('error', {
                            message: 'دریافت کد مالیاتی با خطا مواجه شد',
                        });
                    });
            }}
        >
            استعلام کد مالیاتی
        </LoadingButton>
    );
};

export default AddressCard;
